import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
} from 'react-native';
import DLOModal from './DLOModal';

// Text input component matching the design
const DLOTextInput = ({ 
  label, 
  placeholder, 
  value, 
  onChangeText, 
  required = false,
  keyboardType = 'default'
}: {
  label: string;
  placeholder: string;
  value: string;
  onChangeText: (text: string) => void;
  required?: boolean;
  keyboardType?: 'default' | 'numeric' | 'decimal-pad';
}) => (
  <View style={styles.inputContainer}>
    <View style={styles.labelContainer}>
      <Text style={styles.label}>{label}</Text>
      {required && <Text style={styles.required}>*Required</Text>}
    </View>
    <TextInput
      style={styles.textInput}
      placeholder={placeholder}
      placeholderTextColor="#A0A0A0"
      value={value}
      onChangeText={onChangeText}
      multiline={false}
      keyboardType={keyboardType}
      inputMode={keyboardType === 'decimal-pad' ? 'decimal' : 'text'}
    />
  </View>
);

export interface DLOMassUpdateModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (hours: string) => void;
  isLoading?: boolean;
}

const DLOMassUpdateModal: React.FC<DLOMassUpdateModalProps> = ({
  visible,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const [hours, setHours] = useState('');

  const handleSave = () => {
    if (hours.trim()) {
      onSave(hours.trim());
      setHours(''); // Reset the input
    }
  };

  const handleClose = () => {
    setHours(''); // Reset the input when closing
    onClose();
  };

  const handleHoursChange = (text: string) => {
    // Allow only numeric input with decimals
    const numericValue = text.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return; // Don't allow more than one decimal point
    }
    setHours(numericValue);
  };

  const isValidHours = hours.trim() !== '' && !isNaN(parseFloat(hours)) && parseFloat(hours) >= 0;

  return (
    <DLOModal
      visible={visible}
      onClose={handleClose}
      title="Mass Update"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onPrimaryPress={handleSave}
      onSecondaryPress={handleClose}
      primaryButtonLoading={isLoading}
      primaryButtonDisabled={!isValidHours}
      maxWidth={464} // Based on Figma design
      showCloseIcon={true}
      titleAlign="left"
    >
      <DLOTextInput
        label="Hour"
        placeholder="2"
        value={hours}
        onChangeText={handleHoursChange}
        required
        keyboardType="decimal-pad"
      />
    </DLOModal>
  );
};

const styles = StyleSheet.create({
  inputContainer: {
    gap: 8,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  label: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  required: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 10,
    fontWeight: '500',
    lineHeight: 14,
  },
  textInput: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    minHeight: 40,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
});

export default DLOMassUpdateModal;